import { CANONICAL_STATES, type Job, type Pipeline } from "@career-ops-amplify/shared";
import { isInboxJob } from "@/lib/job-ui";

export interface PipelineAnalytics {
  totalJobs: number;
  inPipeline: number;
  unprocessed: number;
  active: number;
  interviews: number;
  offers: number;
  rejected: number;
  responseRate: string;
  interviewRate: string;
  offerRate: string;
  applyRate: string;
  avgScore: string;
  stageCounts: Record<string, number>;
  scoreBuckets: [number, number, number, number, number];
}

export function getPipelineForJob(
  job: Job,
  pipelines: Pipeline[],
): Pipeline | undefined {
  if (job.pipelineId) {
    const byId = pipelines.find(
      (p) => p.id === job.pipelineId || p.pipelineId === job.pipelineId,
    );
    if (byId) return byId;
  }
  return pipelines.find((p) => p.jobId === job.id);
}

export function computePipelineAnalytics(
  jobs: Job[],
  pipelines: Pipeline[],
): PipelineAnalytics {
  const activePipelines = pipelines.filter((p) => p.status !== "archived");
  const pipedJobs = jobs.filter((j) => {
    if (!j.pipelineId) return false;
    const pipeline = getPipelineForJob(j, pipelines);
    return pipeline && pipeline.status !== "archived";
  });
  const unprocessed = jobs.filter((j) => isInboxJob(j)).length;

  const stageCounts: Record<string, number> = Object.fromEntries(
    CANONICAL_STATES.map((s) => [s, 0]),
  );

  for (const job of pipedJobs) {
    const stage = getPipelineForJob(job, activePipelines)?.currentStage ?? "Evaluated";
    stageCounts[stage] = (stageCounts[stage] ?? 0) + 1;
  }

  const evaluated = stageCounts.Evaluated ?? 0;
  const applied = stageCounts.Applied ?? 0;
  const responded = stageCounts.Responded ?? 0;
  const interviews = stageCounts.Interview ?? 0;
  const offers = stageCounts.Offer ?? 0;
  const rejected = stageCounts.Rejected ?? 0;
  const discarded = stageCounts.Discarded ?? 0;
  const skipped = stageCounts.SKIP ?? 0;

  const active = applied + responded + interviews;
  const submitted = applied + responded + interviews + offers + rejected;
  const evaluatedTotal = pipedJobs.length - skipped - discarded;

  const pct = (n: number, d: number) =>
    d > 0 ? ((n / d) * 100).toFixed(1) : "0.0";

  const scores = pipedJobs
    .map((j) => j.score)
    .filter((s): s is number => s !== undefined && !Number.isNaN(s));
  const avgScore =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : "—";

  const scoreBuckets: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  for (const job of pipedJobs) {
    const n = job.score ?? 0;
    if (n >= 4) scoreBuckets[4]++;
    else if (n >= 3) scoreBuckets[3]++;
    else if (n >= 2) scoreBuckets[2]++;
    else if (n >= 1) scoreBuckets[1]++;
    else scoreBuckets[0]++;
  }

  return {
    totalJobs: jobs.length,
    inPipeline: pipedJobs.length,
    unprocessed,
    active,
    interviews,
    offers,
    rejected,
    responseRate: pct(responded + interviews + offers, submitted),
    interviewRate: pct(interviews, submitted),
    offerRate: pct(offers, submitted),
    applyRate: pct(applied + responded + interviews + offers + rejected, evaluatedTotal),
    avgScore,
    stageCounts: {
      ...stageCounts,
      Evaluated: evaluated,
      Applied: applied,
      Responded: responded,
      Interview: interviews,
      Offer: offers,
      Rejected: rejected,
      Discarded: discarded,
      SKIP: skipped,
    },
    scoreBuckets,
  };
}

/** Group jobs by ISO week (Monday start) for activity charts. */
export function computeWeeklyActivity(
  jobs: Job[],
  weeks = 8,
): { labels: string[]; values: number[] } {
  const counts = new Map<string, number>();

  for (const job of jobs) {
    const d = job.createdAt;
    if (!d) continue;
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) continue;

    const monday = new Date(date);
    const day = (date.getDay() + 6) % 7;
    monday.setDate(date.getDate() - day);
    const key = monday.toISOString().slice(0, 10);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const sorted = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  const slice = sorted.slice(-weeks);
  return {
    labels: slice.map(([k]) => {
      const d = new Date(k);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }),
    values: slice.map(([, v]) => v),
  };
}

/** Top companies by job count in the pipeline. */
export function topCompanies(
  jobs: Job[],
  pipelines: Pipeline[] = [],
  limit = 5,
): Array<{ company: string; count: number }> {
  const counts = new Map<string, number>();
  for (const job of jobs.filter((j) => j.pipelineId)) {
    const pipeline = getPipelineForJob(job, pipelines);
    if (pipeline?.status === "archived") continue;
    const name = job.company || "Unknown";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([company, count]) => ({ company, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}
